/**
 * Pure selectors extracted from `editorStore.ts` (Plan §8 Phase 2.6 —
 * editorStore size reduction, 2026-05-19).
 *
 * Why this split: `editorStore.ts` exceeded 4300 LOC. Pure selectors
 * (those that read only `EditorShellState` / `EditorStore` arguments
 * and the small module-scope sentinels declared here) have no
 * intra-store dependencies, so they move out cleanly. Selectors that
 * reach into `useEditorStore.getState()` or intra-file helpers
 * (`selectSelectedEntities`, `selectChpropOptions`) stay in
 * `editorStore.ts` to avoid circular imports.
 *
 * `editorStore.ts` re-exports every selector defined here so existing
 * callers (~73 import sites across the app) keep working without
 * touching their import paths. This file is the source of truth; the
 * re-exports are mirrors.
 */

import {
  type DimensionStyle,
  type DrawingDocument,
} from "@dxf-ai-editor/drawing-model";

import {
  type DrawingSpace,
  type EditorShellState,
  type EntityPreview,
  type SessionUser,
} from "@dxf-ai-editor/contracts";

// PR-35R streaming-wire fix — shared empty-array sentinel returned by
// `selectEntitiesInActiveSpace` when no entities match the active space
// (huge-import path: `state.drawing.entities === []` because the shell
// preview list was capped by `SHELL_ENTITY_PREVIEW_THRESHOLD`). Without
// this, every App.tsx render produces a fresh `[]` from `.filter()`,
// changing the `entities` prop identity passed into Scene3DStage. That
// invalidates `useSceneEffects`'s bulk-load dep array on every
// `entityMetadataIndexVersion` bump / dispatch / status-text tick —
// re-running the cache-direct bulk-load (which lifts 100k+ entities
// through the visible-set cache synchronously and OOMs the renderer).
// Returning the same `EMPTY_ENTITIES_IN_SPACE` sentinel keeps prop
// identity stable so the effect only re-runs when something actually
// changed.
export const EMPTY_ENTITIES_IN_SPACE: EntityPreview[] = [];

// Style-table empty sentinels — same identity-stability rationale as
// `EMPTY_ENTITIES_IN_SPACE`. React effect deps that subscribe to
// these arrays would re-fire on every render without the shared
// reference.
export const emptyTextStyles: DrawingDocument["styleTables"]["textStyles"] = [];
export const emptyDimensionStyles: DrawingDocument["styleTables"]["dimensionStyles"] =
  [];
export const emptyMultileaderStyles: DrawingDocument["styleTables"]["multileaderStyles"] =
  [];

export function selectCurrentUser(state: EditorShellState): SessionUser {
  const byEmail = state.session.users.find(
    (user) => user.email === state.session.currentUserEmail,
  );
  const fallback = state.session.users[0];
  if (!byEmail && !fallback) {
    throw new Error("selectCurrentUser: session.users is empty");
  }
  return byEmail ?? (fallback as SessionUser);
}

export function selectActiveSpace(state: EditorShellState): DrawingSpace {
  const byId = state.drawing.spaces.find(
    (space) => space.id === state.drawing.activeSpaceId,
  );
  const fallback = state.drawing.spaces[0];
  if (!byId && !fallback) {
    throw new Error("selectActiveSpace: drawing.spaces is empty");
  }
  return byId ?? (fallback as DrawingSpace);
}

export function selectEntitiesInActiveSpace(
  state: EditorShellState,
): EntityPreview[] {
  // Phase FIN.1 — REFEDIT surrounds the working set with the host
  // space's geometry rendered at reduced opacity. While a refedit space
  // is active we union its own entities (the working set) with the
  // entities of the previous active space so the renderer can tint the
  // surrounding ones. Outside REFEDIT this falls back to the regular
  // single-space filter.
  const activeSpace = state.drawing.spaces.find(
    (space) => space.id === state.drawing.activeSpaceId,
  );
  if (activeSpace?.kind === "refedit") {
    const previousId = activeSpace.refeditPreviousSpaceId;
    const previousIdSafe = previousId ?? "";
    // PR-35R streaming-wire fix — fast-exit on empty input to keep
    // stable identity (same rationale as the non-refedit branch below).
    if (state.drawing.entities.length === 0) return EMPTY_ENTITIES_IN_SPACE;
    const out = state.drawing.entities.filter(
      (entity) =>
        entity.spaceId === state.drawing.activeSpaceId ||
        entity.spaceId === previousIdSafe,
    );
    return out.length === 0 ? EMPTY_ENTITIES_IN_SPACE : out;
  }
  // PR-V3 — when state.drawing.entities is empty (huge-import path),
  // the cache-direct render in Scene3DStage drives the scene; this
  // selector returns an empty array so callers don't iterate over a
  // hundred-thousand-entity reactive list. Per-entity payload still
  // available via getEntityById(id).
  //
  // PR-35R streaming-wire fix — when the input list is empty (huge-
  // import path) skip `.filter` entirely and return the module-scope
  // sentinel. Critical for `useSceneEffects`'s bulk-load effect dep
  // stability: a fresh `[]` on every render would re-trigger the
  // cache-direct render path which lifts every metadata-indexed entity
  // through `buildVisibleRenderables` synchronously (OOM on 100k+).
  if (state.drawing.entities.length === 0) return EMPTY_ENTITIES_IN_SPACE;
  const out = state.drawing.entities.filter(
    (entity) => entity.spaceId === state.drawing.activeSpaceId,
  );
  return out.length === 0 ? EMPTY_ENTITIES_IN_SPACE : out;
}

export function selectModelSpaceEntities(
  state: EditorShellState,
): EntityPreview[] {
  const modelSpace = state.drawing.spaces.find((s) => s.kind === "model");
  if (!modelSpace) return EMPTY_ENTITIES_IN_SPACE;
  // PR-35R streaming-wire fix — return shared sentinel when nothing
  // matches so prop identity stays stable (see EMPTY_ENTITIES_IN_SPACE
  // JSDoc on `selectEntitiesInActiveSpace`).
  if (state.drawing.entities.length === 0) return EMPTY_ENTITIES_IN_SPACE;
  const out = state.drawing.entities.filter(
    (entity) => entity.spaceId === modelSpace.id,
  );
  return out.length === 0 ? EMPTY_ENTITIES_IN_SPACE : out;
}

export function selectMatchpropSourceEntity(
  state: EditorShellState,
): EntityPreview | null {
  return (
    state.drawing.entities.find(
      (entity) => entity.id === state.propertyWorkflows.matchpropSourceEntityId,
    ) ?? null
  );
}

/**
 * NOTE: `DimensionStyle` re-exported here because consumers downstream
 * of editorStore.ts use it via the same barrel surface. Kept as a
 * named re-export so editorStore.ts's existing re-exports cover
 * callers without a TypeScript-level circular dep.
 */
export type { DimensionStyle };

/**
 * Style-table selectors. Read `state.geometryDocument.styleTables`
 * directly — no `useEditorStore.getState()` indirection — so they
 * live cleanly in this file.
 *
 * The argument type is `{ geometryDocument: DrawingDocument }`
 * structurally so callers can pass either the full `EditorStore`
 * shape (Zustand root) or any subset that includes the
 * `geometryDocument` slice. This mirrors the original callsite
 * convention from editorStore.ts.
 */
export function selectTextStyles(state: {
  geometryDocument: DrawingDocument;
}): DrawingDocument["styleTables"]["textStyles"] {
  return state.geometryDocument.styleTables?.textStyles ?? emptyTextStyles;
}

export function selectDimensionStyles(state: {
  geometryDocument: DrawingDocument;
}): DrawingDocument["styleTables"]["dimensionStyles"] {
  return (
    state.geometryDocument.styleTables?.dimensionStyles ?? emptyDimensionStyles
  );
}

export function selectMultileaderStyles(state: {
  geometryDocument: DrawingDocument;
}): DrawingDocument["styleTables"]["multileaderStyles"] {
  return (
    state.geometryDocument.styleTables?.multileaderStyles ??
    emptyMultileaderStyles
  );
}
